import React from "react";
import Image from "next/image";
import { ITeamMemberFields } from "src/types/generated/contentful";
import styled from "styled-components";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { between, down } from "styled-breakpoints";

import ImageTopic from "../../../atoms/ImageTopic";
import { useBreakpoint } from "styled-breakpoints/react-styled";
import { formatPhoneNumber } from "./CardLayout";
import { Box, Flex, Heading, Button, Link, Stack } from "@chakra-ui/react";
import { ChevronLeftIcon, EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import { AssetFields } from "contentful";

type TeamMemberProps = { "data-layoutid"?: string } & ITeamMemberFields;

export default function PageLayout({
  phone,
  email,
  name,
  jobTitle,
  bio,
  slug,
  accreditationImage,
  licenseNumbers,
  nationalProducerNumber,
  profileImage,
}: TeamMemberProps) {
  const profileImagef = profileImage?.fields as AssetFields;
  const imageUrl = profileImage ? `https:${profileImagef?.file?.url}` : "";

  const isMobile = useBreakpoint(down("sm"));
  const isTablet = useBreakpoint(between("sm", "lg"));

  return (
    <StylingWrapper>
      <Box p={10}>
        <Link href={`javascript:history.back()`}>
          {" "}
          <ChevronLeftIcon /> Back To All Team Members
        </Link>
        <Flex direction={["column", "row"]}>
          <Box
            w={["100%", "50%"]}
            alignItems="center"
            justifyContent="center"
            p={3}
          >
            <ImageTopic
              image={profileImage}
              flexDisplayHorizontalAlignment="center"
              flexDisplayVerticalAlignment="top"
            />
          </Box>
          <Stack w={["100%", "50%"]} p={3}>
            <Box mb={10} textAlign={["center", "left"]}>
              <Heading as="h1">{name}</Heading>
              <Heading as="h2">{jobTitle}</Heading>
            </Box>

            <Link href={`mailto:${email}`}>
              <EmailIcon mr={3} />
              {email}
            </Link>

            <Link href={`tel:${phone}`}>
              <PhoneIcon mr={3} />
              {formatPhoneNumber(phone?.toString() || "")}{" "}
            </Link>

            <div>{documentToReactComponents(bio as any)}</div>
          </Stack>
        </Flex>
        <Box textAlign="center" p={5} mt={10} mb={10}>
          {nationalProducerNumber ? (
            <>
              <Heading as="h2">
                {`${name}'s Licensed States And License Numbers National Producer`}
              </Heading>
              <Heading as="h3">Number: {nationalProducerNumber}</Heading>
            </>
          ) : (
            <span />
          )}
        </Box>
        <FlexWrapper>
          {getLicenseTable(licenseNumbers, getSplit(isMobile, isTablet))}
        </FlexWrapper>
      </Box>
    </StylingWrapper>
  );
}

const getSplit = (
  isMobile: boolean | null,
  isTablet: boolean | null
): number => {
  if (isMobile) {
    return 1;
  } else if (isTablet) {
    return 2;
  }
  return 3;
};

const getLicenseTable = (
  licenseNumbers: any,
  iterateCount: number
): React.ReactElement[] => {
  let arr: React.ReactElement[] = [];
  if (licenseNumbers && licenseNumbers.length > 0) {
    const count = licenseNumbers.length / iterateCount;
    let lastCount = 0;
    [...Array(iterateCount)].forEach((_, i) => {
      const nl = licenseNumbers.slice(lastCount, lastCount + count);
      lastCount += count;
      arr.push(
        <LicenseTable width={`${100 / iterateCount - 5}%`}>
          <thead>
            <tr>
              <th>State</th>
              <th>License Number</th>
            </tr>
          </thead>
          <tbody>
            {nl.map((licenseNumber: any, i: number) => {
              return (
                <tr key={i}>
                  <td>{licenseNumber["State"]}</td>
                  <td>{licenseNumber["License Number"]}</td>
                </tr>
              );
            })}
          </tbody>
        </LicenseTable>
      );
    });
  } else {
    return [<div key={0}></div>];
  }

  return arr;
};

const StylingWrapper = styled.section`
  padding: 0 1em;

  h1 {
    font-size: calc(${({ theme }) => theme.typography.h1.fontSize}rem - 1.8rem);
    padding: 0;
    margin: 2rem 0;
    line-height: calc(
      ${({ theme }) => theme.typography.h1.fontSize}rem - 1.8rem
    );
  }
  h2 {
    font-size: calc(${({ theme }) => theme.typography.h2.fontSize}rem - 1.8rem);
    font-weight: calc(${({ theme }) => theme.typography.h2.fontWeight} - 200);
    padding: 0;
    margin: 2rem 0;
    line-height: calc(
      ${({ theme }) => theme.typography.h2.fontSize}rem - 1.8rem
    );
  }
  h3 {
    font-size: calc(${({ theme }) => theme.typography.h3.fontSize}rem - 1rem);
    font-weight: calc(${({ theme }) => theme.typography.h2.fontWeight} - 300);

    padding: 0;
    margin: 2rem 0;
  }
`;

const StyledButton = styled(Button)`
  color: #a8a8a8;
  margin: 2em;
  a {
    color: #a8a8a8;
    font-size: 1rem;
    text-decoration-color: rgba(0, 0, 0, 0.3);
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1em;
`;

const CenterItems = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 1em;
  h2 {
    width: 60%;
  }
`;

const LicenseTable = styled.table`
  width: ${(props) => props.width || "100%"};
  border-collapse: collapse;
  tr {
    text-align: left;
  }

  th,
  td {
    padding: 0 1em;
    height: 80px;
  }
  thead tr {
    th {
      background-color: ${(props) =>
        props.theme.palette.common.darkBlue} !important;
      color: #fff;
    }
  }

  tr:nth-child(even) {
    background: #f2f2f2;
  }
  tr:nth-child(odd) {
    background: #fff;
  }
`;
